import { useState, useEffect, Fragment, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ClickableAvatar } from "../components/ClickableAvatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import { MembershipModal } from "../components/MembershipModal";
import { AddPaymentModal } from "../components/AddPaymentModal";
import { MemberProgressTab } from "../components/MemberProgressTab";
import { MemberWorkoutTab } from "../components/MemberWorkoutTab";
import { MemberFollowupsTab } from "../components/MemberFollowupsTab";
import { QuickAttendanceDialog } from "../components/QuickAttendanceDialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Phone,
  Mail,
  Calendar,
  CreditCard,
  Edit2,
  Plus,
  User,
  MapPin,
  Heart,
  Target,
  Clock,
  DollarSign,
  CalendarCheck,
  TrendingUp,
  Dumbbell,
  MessageSquare,
  FileText,
  PenLine,
  Briefcase,
  Users,
  Package,
  CalendarDays,
  AlertCircle,
  Banknote,
  Tag,
  Trash2,
  Activity,
  ChevronDown,
  Building,
  GitBranch,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { getStateLabel } from "../lib/locationData";
import { formatTimeAMPM, getEffectiveMembershipStatus } from "../lib/utils";
import { getOrgTimezone } from "../lib/timezone";
import { WhatsApp } from "../components/ui/whatsapp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Returns { bmi, category, color } or null if height/weight missing */
function calcBMI(height, weight) {
  if (!height || !weight || height <= 0 || weight <= 0) return null;
  const heightM = height / 100;
  const bmi = weight / (heightM * heightM);
  const rounded = Math.round(bmi * 10) / 10;

  let category, color;
  if (bmi < 18.5) {
    category = "Underweight";
    color = "bg-blue-500/15 text-blue-400 border-blue-500/20";
  } else if (bmi < 25) {
    category = "Normal";
    color = "bg-emerald-500/15 text-emerald-500 border-emerald-500/20";
  } else if (bmi < 30) {
    category = "Overweight";
    color = "bg-amber-500/15 text-amber-500 border-amber-500/20";
  } else {
    category = "Obese";
    color = "bg-red-500/15 text-red-500 border-red-500/20";
  }
  return { bmi: rounded, category, color };
}

export default function MemberDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, branches, organization } = useAuth();
  const orgTimezone = getOrgTimezone(organization);

  const activeTab = searchParams.get("tab") || "details";

  const canEdit = hasPermission("edit_member");
  const canViewMemberships = hasPermission("view_memberships");
  const canViewPayments =
    hasPermission("view_payments") || hasPermission("view_memberships");
  const canAddMembership = hasPermission("create_membership");
  const canManageMembership = hasPermission("manage_memberships");
  const canAddPayment = hasPermission("add_payment");
  const canDeletePayment = hasPermission("add_payment");
  const canViewFollowups =
    hasPermission("view_followups") || hasPermission("manage_followups");
  const canViewAttendance = hasPermission("view_attendance");
  const canViewWorkouts = hasPermission("view_workouts");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [editMembership, setEditMembership] = useState(null);
  const [isRenewalModal, setIsRenewalModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMembership, setPaymentMembership] = useState(null);
  const [expandedMembershipRows, setExpandedMembershipRows] = useState({});
  const [expandedPaymentRows, setExpandedPaymentRows] = useState({});
  const [attendance, setAttendance] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendancePage, setAttendancePage] = useState(1);
  const [attendancePageSize, setAttendancePageSize] = useState(20);
  const [attendanceTotal, setAttendanceTotal] = useState(0);
  const [showAttendanceDialog, setShowAttendanceDialog] = useState(false);
  const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

  const handleTabClick = (e) => {
    e.currentTarget.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  };

  const toggleMembershipRow = (membershipId) => {
    setExpandedMembershipRows((prev) => ({
      ...prev,
      [membershipId]: !prev[membershipId],
    }));
  };

  const togglePaymentRow = (paymentId) => {
    setExpandedPaymentRows((prev) => ({
      ...prev,
      [paymentId]: !prev[paymentId],
    }));
  };

  useEffect(() => {
    fetchMember();
    fetchPackages();
  }, [id]);

  const fetchAttendance = useCallback(async () => {
    if (!id) return;
    setAttendanceLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", attendancePage);
      params.append("pageSize", attendancePageSize);
      const res = await api.get(
        `/attendance/member/${id}?${params.toString()}`,
      );
      setAttendance(res.data.attendance || []);
      setAttendanceTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load attendance records");
    } finally {
      setAttendanceLoading(false);
    }
  }, [id, attendancePage, attendancePageSize]);

  useEffect(() => {
    setAttendancePage(1);
  }, [id, activeTab]);

  useEffect(() => {
    if (activeTab === "attendance" && canViewAttendance) {
      fetchAttendance();
    }
  }, [activeTab, canViewAttendance, fetchAttendance]);

  const fetchMember = async () => {
    try {
      const res = await api.get(`/members/${id}`);
      setData(res.data);
    } catch {
      toast.error("Member not found");
      navigate("/members");
    } finally {
      setLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      const res = await api.get("/packages");
      setPackages(res.data.packages);
    } catch {}
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("Delete this payment?")) return;
    try {
      await api.delete(`/memberships/payments/${paymentId}`);
      toast.success("Payment deleted");
      fetchMember();
    } catch {
      toast.error("Failed to delete payment");
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
          <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
            Loading
          </p>
        </div>
      </div>
    );

  if (!data) return null;

  const { member, memberships, payments } = data;
  const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const pending = memberships.reduce((s, m) => s + (m.remainingAmount || 0), 0);
  // Use computed status so a past-endDate Active membership is not treated as active
  const activeMembership = memberships.find(
    (m) =>
      getEffectiveMembershipStatus(m, orgTimezone) === "Active" ||
      getEffectiveMembershipStatus(m, orgTimezone) === "Partial",
  );
  const hasExpiredMemberships = memberships.some(
    (m) => getEffectiveMembershipStatus(m, orgTimezone) === "Expired",
  );
  const expiredMembershipsSorted = [...memberships]
    .filter((m) => getEffectiveMembershipStatus(m, orgTimezone) === "Expired")
    .sort((a, b) => new Date(b.endDate) - new Date(a.endDate));
  const latestExpiredMembership = expiredMembershipsSorted[0] || null;

  const bmiData = calcBMI(member.height, member.weight);

  const getPackageName = (ms) =>
    ms.packageName || packages.find((p) => p.id === ms.packageId)?.name || "-";

  const statusColor = (s) => {
    const map = {
      Active: "bg-emerald-500/15 text-emerald-500 border-emerald-500/20",
      Expired: "bg-red-500/15 text-red-500 border-red-500/20",
      Partial: "bg-amber-500/15 text-amber-500 border-amber-500/20",
      Pending: "bg-blue-500/15 text-blue-500 border-blue-500/20",
      Inactive: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
      Renewed: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
    };
    return map[s] || "bg-zinc-500/15 text-zinc-400 border-zinc-500/20";
  };

  const renderAmount = (ms) => {
    const final = ms.finalAmount ?? ms.amount ?? 0;
    const original = ms.amount ?? 0;
    const hasDiscount =
      ms.discountValue > 0 &&
      ms.finalAmount != null &&
      ms.finalAmount < original;
    return (
      <div className="flex flex-col gap-0.5">
        <span className="font-mono font-medium">₹{final.toLocaleString()}</span>
        {hasDiscount && (
          <div className="flex items-center gap-1">
            <span className="font-mono text-xs text-muted-foreground line-through">
              ₹{original.toLocaleString()}
            </span>
            <span className="inline-flex items-center gap-0.5 text-[10px] font-mono text-emerald-500">
              <Tag className="h-2.5 w-2.5" />
              {ms.discountType === "percentage"
                ? `${ms.discountValue}% off`
                : `₹${ms.discountValue} off`}
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderMembershipDetails = (ms) => {
    const msPayments = (payments || []).filter((p) => p.membershipId === ms.id);
    const hasDiscount =
      ms.discountValue > 0 &&
      ms.finalAmount != null &&
      ms.finalAmount < (ms.amount ?? 0);
    const effectiveStatus = getEffectiveMembershipStatus(ms, orgTimezone);
    return (
      <div className="text-sm text-foreground bg-secondary/5">
        {/* Action Buttons */}
        <div className="flex items-center gap-2 p-2 border-b border-border/40 bg-secondary/10">
          {canAddPayment && ms.remainingAmount > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5 bg-background hover:bg-secondary/40 transition-colors text-amber-500 hover:text-amber-600 border-amber-500/20"
              onClick={(e) => {
                e.stopPropagation();
                setPaymentMembership(ms);
                setShowPaymentModal(true);
              }}
            >
              <Banknote className="h-3.5 w-3.5" /> Pay
            </Button>
          )}
          {canManageMembership && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5 bg-background hover:bg-secondary/40 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setEditMembership(ms);
                setIsRenewalModal(false);
                setShowMembershipModal(true);
              }}
            >
              <Edit2 className="h-3.5 w-3.5" /> Edit{" "}
              <span className="hidden md:inline">Membership</span>
            </Button>
          )}
          {/* Renew button for expired memberships */}
          {canAddMembership && effectiveStatus === "Expired" && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5 bg-background hover:bg-amber-500/10 transition-colors text-amber-500 hover:text-amber-600 border-amber-500/30"
              onClick={(e) => {
                e.stopPropagation();
                setEditMembership(ms);
                setIsRenewalModal(true);
                setShowMembershipModal(true);
              }}
            >
              <CreditCard className="h-3.5 w-3.5" /> Renew{" "}
              <span className="hidden md:inline">Membership</span>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5 bg-background hover:bg-secondary/40 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/memberships/${ms.id}`);
            }}
          >
            <CreditCard className="h-3.5 w-3.5" /> View Details
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3">
          {/* Package & Duration Details */}
          <div className="space-y-2 p-3">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Package & Pricing
            </p>
            <div className="space-y-1 mt-1 text-xs">
              <p className="font-semibold text-sm text-foreground">
                {getPackageName(ms)}
              </p>
              <div className="flex flex-col gap-0.5 text-muted-foreground mt-1">
                <p>
                  <span className="font-mono text-muted-foreground">
                    Base Amount:
                  </span>{" "}
                  ₹{(ms.amount ?? 0).toLocaleString()}
                </p>
                {hasDiscount && (
                  <>
                    <p>
                      <span className="font-mono text-muted-foreground">
                        Discount:
                      </span>{" "}
                      <span className="text-emerald-500">
                        {ms.discountType === "percentage"
                          ? `${ms.discountValue}% off`
                          : `₹${ms.discountValue} off`}
                      </span>
                    </p>
                    <p className="font-medium text-foreground">
                      <span className="font-mono text-muted-foreground">
                        Final Amount:
                      </span>{" "}
                      ₹{(ms.finalAmount ?? 0).toLocaleString()}
                    </p>
                  </>
                )}
                <p className="mt-1 font-mono text-[11px]">
                  <span className="font-sans text-xs">Duration:</span>{" "}
                  {ms.startDate?.slice(0, 10)} to {ms.endDate?.slice(0, 10)}
                </p>
              </div>
            </div>
          </div>
          {/* Payment Status & History */}
          <div className="space-y-2 p-3 lg:border-l lg:border-border border-t md:border-t-0">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Payment Summary
            </p>
            <div className="space-y-1 mt-1 text-xs text-muted-foreground font-sans">
              <p>
                <span className="font-mono">Total Paid:</span>{" "}
                <span className="text-emerald-500 font-semibold font-mono">
                  ₹{(ms.paidAmount || 0).toLocaleString()}
                </span>
              </p>
              <p>
                <span className="font-mono">Total Due:</span>{" "}
                <span
                  className={`font-semibold font-mono ${ms.remainingAmount > 0 ? "text-amber-500" : "text-emerald-500"}`}
                >
                  ₹{(ms.remainingAmount || 0).toLocaleString()}
                </span>
              </p>
              {ms.status === "Partial" && ms.partialPaymentDate && (
                <p className="text-amber-500 font-mono text-[11px]">
                  <span className="font-sans text-xs text-muted-foreground">
                    Due Date:
                  </span>{" "}
                  {ms.partialPaymentDate.slice(0, 10)}
                </p>
              )}
              {msPayments.length > 0 ? (
                <div className="mt-2 space-y-1">
                  <p className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground border-b border-border/30 pb-0.5 mb-1">
                    Receipts
                  </p>
                  {msPayments.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between text-[11px] font-mono bg-background/30 rounded px-1.5 py-0.5"
                    >
                      <span>
                        {p.date?.slice(0, 10)} ({p.paymentMode})
                      </span>
                      <span className="text-emerald-500">
                        ₹{p.amount?.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] italic text-muted-foreground mt-2">
                  No payments recorded under this plan.
                </p>
              )}
            </div>
          </div>
          {/* Notes & Comments */}
          <div className="space-y-2 p-3 lg:border-l lg:border-border border-t md:border-t-0">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Plan Notes
            </p>
            <p className="text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap mt-1">
              {ms.notes || "No plan-specific notes provided."}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderPaymentDetails = (p) => {
    return (
      <div className="text-sm text-foreground bg-secondary/5">
        <div className="flex items-center gap-2 p-2 border-b border-border/40 bg-secondary/10">
          {canDeletePayment && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5 bg-background hover:bg-secondary/40 transition-colors text-destructive hover:text-destructive border-red-500/20"
              onClick={(e) => {
                e.stopPropagation();
                handleDeletePayment(p.id);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete Payment
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3">
          <div className="space-y-1">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Payment Details
            </p>
            <div className="text-xs space-y-1 mt-1 text-muted-foreground">
              <p>
                <span className="font-mono text-muted-foreground">
                  Payment ID:
                </span>{" "}
                <span className="font-mono">{p.id}</span>
              </p>
              <p>
                <span className="font-mono text-muted-foreground">
                  Transaction ID:
                </span>{" "}
                <span className="font-mono">{p.transactionId || "—"}</span>
              </p>
              <div>
                <span className="font-mono text-muted-foreground">
                  Method/Mode:
                </span>{" "}
                <Badge
                  variant="outline"
                  className="text-[10px] py-0 px-1 font-mono"
                >
                  {p.paymentMode}
                </Badge>
              </div>
              <p>
                <span className="font-mono text-muted-foreground">Date:</span>{" "}
                <span className="font-mono">
                  {p.date?.slice(0, 10)}{" "}
                  {p.date?.length > 10 ? p.date.slice(11, 16) : ""}
                </span>
              </p>
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Payment Notes
            </p>
            <p className="text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap mt-1">
              {p.notes || "No payment-specific notes provided."}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderPaymentDate = (dateStr) => {
    if (!dateStr) return <span className="text-muted-foreground">—</span>;
    const date = dateStr.slice(0, 10);
    const timePart = dateStr.length > 10 ? dateStr.slice(11, 16) : null;
    return (
      <div className="flex flex-col gap-0.5">
        <span className="font-mono">{date}</span>
        {timePart && (
          <span className="text-[11px] font-mono text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatTimeAMPM(timePart)}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="member-details-page">
      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/members")}
            className="rounded-xl shrink-0 h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-base sm:text-2xl md:text-3xl font-black font-heading tracking-tight uppercase leading-tight truncate">
              Member Details
            </h1>
            <p className="hidden sm:block text-xs sm:text-sm text-muted-foreground">
              View and manage member information
            </p>
          </div>
        </div>
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/members/${id}/edit`)}
            className="font-heading uppercase tracking-wide text-xs shrink-0"
            data-testid="edit-member-btn"
          >
            <Edit2 className="h-4 w-4 sm:mr-2" />
            <span className="">Edit</span>
          </Button>
        )}
      </div>

      {/* ── Profile Card ── */}
      <Card className="bg-card border-border overflow-hidden">
        <div className="h-12 sm:h-16 bg-gradient-to-r from-primary/20 via-primary/10 to-transparent" />
        <CardContent className="px-4 sm:px-6 pb-5 -mt-10 sm:-mt-12">
          <div className="flex flex-col gap-4">
            <div className="flex items-end justify-between gap-3">
              <ClickableAvatar
                src={member.photo}
                name={member.name}
                className="h-20 w-20 sm:h-24 sm:w-24 border-4 border-background shadow-xl shrink-0"
                fallbackClassName="bg-primary text-primary-foreground text-2xl sm:text-3xl font-heading"
              />
              {canViewPayments && (
                <div className="flex gap-4 sm:gap-6 pb-1">
                  <div className="text-center">
                    <p className="text-lg sm:text-2xl font-black font-heading text-emerald-500">
                      ₹{totalPaid.toLocaleString()}
                    </p>
                    <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                      Paid
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg sm:text-2xl font-black font-heading text-amber-500">
                      ₹{pending.toLocaleString()}
                    </p>
                    <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                      Due
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl sm:text-2xl font-black font-heading">
                {member.name}
              </h2>
              {activeMembership ? (
                <Badge
                  className={`${statusColor(getEffectiveMembershipStatus(activeMembership, orgTimezone))} border text-xs font-mono uppercase`}
                >
                  {getEffectiveMembershipStatus(activeMembership, orgTimezone)}
                </Badge>
              ) : (
                <Badge
                  className={`${hasExpiredMemberships ? statusColor("Expired") : statusColor("Pending")} border text-xs font-mono uppercase`}
                >
                  {hasExpiredMemberships ? "Expired" : "No Active Plan"}
                </Badge>
              )}
              {member.biometricId && (
                <Badge variant="outline" className="border-primary/40 text-primary text-xs font-mono" data-testid="member-biometric-badge">
                  Bio ID: {member.biometricId}
                </Badge>
              )}
              {/* Renew button in profile header for expired members */}
              {!activeMembership &&
                hasExpiredMemberships &&
                canAddMembership && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs gap-1.5 font-heading uppercase tracking-wide text-amber-500 hover:text-amber-600 hover:bg-amber-500/10 border-amber-500/30"
                    onClick={() => {
                      setEditMembership(latestExpiredMembership);
                      setIsRenewalModal(true);
                      setShowMembershipModal(true);
                    }}
                    data-testid="renew-membership-btn"
                  >
                    <CreditCard className="h-3 w-3" /> Renew Membership
                  </Button>
                )}
            </div>

            {activeMembership && (
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs -mt-1">
                <span className="inline-flex items-center gap-1 text-primary font-medium">
                  <Package className="h-3.5 w-3.5" />
                  {getPackageName(activeMembership)}
                </span>
                {activeMembership.startDate && activeMembership.endDate && (
                  <span className="inline-flex items-center gap-1 text-muted-foreground font-mono">
                    <CalendarDays className="h-3.5 w-3.5" />
                    {activeMembership.startDate.slice(0, 10)} →{" "}
                    {activeMembership.endDate.slice(0, 10)}
                  </span>
                )}
                {activeMembership.remainingAmount > 0 && (
                  <span className="inline-flex items-center gap-1 text-amber-500 font-mono">
                    <AlertCircle className="h-3.5 w-3.5" />
                    Due: ₹{activeMembership.remainingAmount.toLocaleString()}
                  </span>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground -mt-1">
              {member.phone && (
                <span className="flex items-center gap-2 bg-secondary/40 rounded-xl pl-2 pr-1.5 py-1 border border-border/80 shadow-sm">
                  <span className="flex items-center gap-1.5 font-medium text-foreground">
                    <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                    {member.phone}
                  </span>
                  <span className="flex items-center gap-1 ml-1.5">
                    <a
                      href={`tel:${member.phone}`}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white dark:bg-zinc-900/50 hover:bg-sky-50 dark:hover:bg-sky-950/30 border border-slate-200 dark:border-zinc-800 hover:border-sky-300 dark:hover:border-sky-900/50 shadow-sm transition-all hover:scale-105 active:scale-95"
                      title="Call"
                    >
                      <Phone className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" />
                    </a>
                    <a
                      href={`https://wa.me/${member.phone}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white dark:bg-zinc-900/50 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 border border-slate-200 dark:border-zinc-800 hover:border-emerald-300 dark:hover:border-emerald-900/50 shadow-sm transition-all hover:scale-105 active:scale-95"
                      title="WhatsApp"
                    >
                      <WhatsApp className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                    </a>
                  </span>
                </span>
              )}
              {member.email && (
                <span className="flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  {member.email}
                </span>
              )}
              {member.gender && (
                <span className="flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" />
                  {member.gender}
                </span>
              )}
              {member.dob && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  {member.dob.slice(0, 10)}
                  <span className="ml-1 text-muted-foreground text-xs bg-secondary rounded-full px-2 py-1">
                    {(() => {
                      const age =
                        new Date().getFullYear() -
                        new Date(member.dob).getFullYear();
                      return age;
                    })()}{" "}
                    years old
                  </span>
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Tabs ── */}
      <Tabs
        value={activeTab}
        onValueChange={(val) => setSearchParams({ tab: val })}
        className="space-y-4"
      >
        <div className="overflow-x-auto scrollbar-hide rounded-md px-4 sm:mx-0 sm:px-0 border border-slate-200 w-full">
          <TabsList className="bg-secondary/50 p-1 gap-1 w-max sm:w-auto sm:flex-wrap">
            <TabsTrigger
              value="details"
              className="font-heading uppercase text-xs tracking-wide whitespace-nowrap"
              data-testid="tab-details"
              onClick={handleTabClick}
            >
              <User className="h-3.5 w-3.5 mr-1.5" />
              Details
            </TabsTrigger>
            {canViewMemberships && (
              <TabsTrigger
                value="memberships"
                className="font-heading uppercase text-xs tracking-wide whitespace-nowrap"
                data-testid="tab-memberships"
                onClick={handleTabClick}
              >
                <CreditCard className="h-3.5 w-3.5 mr-1.5" />
                Memberships
              </TabsTrigger>
            )}
            {canViewPayments && (
              <TabsTrigger
                value="payments"
                className="font-heading uppercase text-xs tracking-wide whitespace-nowrap"
                data-testid="tab-payments"
                onClick={handleTabClick}
              >
                <DollarSign className="h-3.5 w-3.5 mr-1.5" />
                Payments
              </TabsTrigger>
            )}
            {canViewAttendance && (
              <TabsTrigger
                value="attendance"
                className="font-heading uppercase text-xs tracking-wide whitespace-nowrap"
                data-testid="tab-attendance"
                onClick={handleTabClick}
              >
                <CalendarCheck className="h-3.5 w-3.5 mr-1.5" />
                Attendance
              </TabsTrigger>
            )}
            <TabsTrigger
              value="progress"
              className="font-heading uppercase text-xs tracking-wide whitespace-nowrap"
              data-testid="tab-progress"
              onClick={handleTabClick}
            >
              <TrendingUp className="h-3.5 w-3.5 mr-1.5" />
              Goal Progress
            </TabsTrigger>
            {canViewWorkouts && (
              <TabsTrigger
                value="workout"
                className="font-heading uppercase text-xs tracking-wide whitespace-nowrap"
                data-testid="tab-workout"
                onClick={handleTabClick}
              >
                <Dumbbell className="h-3.5 w-3.5 mr-1.5" />
                Workout
              </TabsTrigger>
            )}
            {canViewFollowups && (
              <TabsTrigger
                value="followups"
                className="font-heading uppercase text-xs tracking-wide whitespace-nowrap"
                data-testid="tab-followups"
                onClick={handleTabClick}
              >
                <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                Followups
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        {/* ── Details Tab ── */}
        <TabsContent value="details">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-heading uppercase tracking-wide flex items-center gap-2">
                  <User className="h-4 w-4 text-primary" /> Personal Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">
                    Full Name
                  </span>
                  <span className="font-medium text-right">{member.name}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">Gender</span>
                  <span>{member.gender || "-"}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">
                    Date of Birth
                  </span>
                  <span className="font-mono">
                    {member.dob?.slice(0, 10) || "-"}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">Age</span>
                  <span className="font-mono">
                    {(() => {
                      const age =
                        new Date().getFullYear() -
                        new Date(member.dob).getFullYear();
                      return age;
                    })()}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">
                    Joining Date
                  </span>
                  <span className="font-mono">
                    {member.joiningDate?.slice(0, 10) || "-"}
                  </span>
                </div>
                {member.biometricId && (
                  <div className="flex justify-between gap-2">
                    <span className="text-muted-foreground shrink-0">
                      Biometric ID
                    </span>
                    <span className="font-mono font-semibold text-primary">
                      {member.biometricId}
                    </span>
                  </div>
                )}
                {(member.professional || member.reference) && (
                  <div className="pt-2 border-t border-border space-y-3">
                    {member.professional && (
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground flex items-center gap-1 shrink-0">
                          <Briefcase className="h-3 w-3" />
                          Professional
                        </span>
                        <span className="text-right">
                          {member.professional}
                        </span>
                      </div>
                    )}
                    {member.reference && (
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground flex items-center gap-1 shrink-0">
                          <Users className="h-3 w-3" />
                          Reference
                        </span>
                        <span className="text-right">{member.reference}</span>
                      </div>
                    )}
                  </div>
                )}
                <div className="pt-2 border-t border-border space-y-3">
                  {member.branchIds && member.branchIds.length > 0 && (
                    <div className="flex justify-between gap-2">
                      <span className="text-muted-foreground shrink-0 flex items-center gap-1">
                        <GitBranch className="h-3.5 w-3.5 text-primary/75" />
                        Assigned Branches
                      </span>
                      <span className="font-medium text-right flex flex-wrap gap-1 justify-end max-w-[60%]">
                        {(branches || [])
                          .filter((b) => member.branchIds.includes(b.id))
                          .map((b) => (
                            <Badge
                              key={b.id}
                              variant="secondary"
                              className="text-[10px] px-1.5 py-0 font-normal"
                            >
                              {b.name}
                            </Badge>
                          ))}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-heading uppercase tracking-wide flex items-center gap-2">
                  <Phone className="h-4 w-4 text-primary" /> Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">Phone</span>
                  <span className="font-mono">{member.phone || "-"}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">Email</span>
                  <span className="truncate text-right">
                    {member.email || "-"}
                  </span>
                </div>
                {member.emergencyContactName && (
                  <>
                    <div className="pt-2 border-t border-border">
                      <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                        Emergency Contact
                      </p>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-muted-foreground shrink-0">
                        Name
                      </span>
                      <span className="text-right">
                        {member.emergencyContactName}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-muted-foreground shrink-0">
                        Relation
                      </span>
                      <span>{member.emergencyContactRelation || "-"}</span>
                    </div>
                    {member.emergencyContactPhone && (
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground shrink-0">
                          Phone
                        </span>
                        <span className="font-mono">
                          {member.emergencyContactPhone || "-"}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-heading uppercase tracking-wide flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" /> Address
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">
                    Address
                  </span>
                  <span className="text-right">{member.address || "-"}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">City</span>
                  <span>{member.city || "-"}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">
                    {getStateLabel(member.country)}
                  </span>
                  <span>{member.state || "-"}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">
                    Pincode
                  </span>
                  <span className="font-mono">{member.pincode || "-"}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">
                    Country
                  </span>
                  <span>{member.country || "-"}</span>
                </div>
              </CardContent>
            </Card>

            {/* ── Fitness Information (with BMI) ── */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-heading uppercase tracking-wide flex items-center gap-2">
                  <Target className="h-4 w-4 text-primary" /> Fitness
                  Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {/* <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">Goal</span>
                  <span className="text-right">{member.goal || "-"}</span>
                </div> */}
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">Height</span>
                  <span className="font-mono">
                    {member.height ? `${member.height} cm` : "-"}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">Weight</span>
                  <span className="font-mono">
                    {member.weight ? `${member.weight} kg` : "-"}
                  </span>
                </div>

                {/* BMI row — only shown when both height & weight are present */}
                {bmiData && (
                  <div className="pt-2 border-t border-border">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground flex items-center gap-1.5 shrink-0">
                        <Activity className="h-3.5 w-3.5" />
                        BMI
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold">
                          {bmiData.bmi}
                        </span>
                        <Badge
                          className={`${bmiData.color} border text-[10px] font-mono uppercase`}
                        >
                          {bmiData.category}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground font-mono mt-1.5 text-right">
                      kg/m² · &lt;18.5 Under · 18.5–24.9 Normal · 25–29.9 Over ·
                      ≥30 Obese
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {member.medicalIssue && (
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-heading uppercase tracking-wide flex items-center gap-2">
                    <Heart className="h-4 w-4 text-red-500" /> Medical
                    Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    {member.medicalIssue}
                  </p>
                </CardContent>
              </Card>
            )}

            {member.notes && (
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-heading uppercase tracking-wide flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" /> Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {member.notes}
                  </p>
                </CardContent>
              </Card>
            )}

            {member.signature && (
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-heading uppercase tracking-wide flex items-center gap-2">
                    <PenLine className="h-4 w-4 text-primary" /> Signature
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <img
                    src={
                      member.signature.startsWith("data:")
                        ? member.signature
                        : `${BACKEND_URL}${member.signature}`
                    }
                    alt="Member Signature"
                    className="max-h-24 w-auto rounded border border-border bg-white p-1"
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ── Memberships Tab ── */}
        {canViewMemberships && (
          <TabsContent value="memberships">
            <Card
              className="bg-card border-border overflow-hidden flex flex-col"
              style={{ minHeight: "400px" }}
            >
              <CardHeader className="flex flex-row items-center justify-between gap-3 py-2 px-4 border-b">
                <CardTitle className="text-sm font-heading uppercase tracking-wide">
                  Memberships History
                </CardTitle>
                {canAddMembership && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditMembership(null);
                      setIsRenewalModal(false);
                      setShowMembershipModal(true);
                    }}
                    className="text-xs font-heading uppercase tracking-wide shrink-0 font-medium mt-0"
                    data-testid="add-membership-btn"
                  >
                    <Plus className="h-4 w-4 sm:mr-1.5" />
                    <span className="hidden sm:inline">Add Membership</span>
                    <span className="sm:hidden">Add</span>
                  </Button>
                )}
              </CardHeader>

              {/* ── Desktop table (hidden below md) ── */}
              <div
                className="hidden md:block overflow-auto flex-1"
                style={{ maxHeight: "calc(100vh - 340px)" }}
              >
                <Table>
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Package</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Paid</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {memberships.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center py-8 text-muted-foreground"
                        >
                          No memberships found
                        </TableCell>
                      </TableRow>
                    ) : (
                      memberships.map((ms) => (
                        <Fragment key={ms.id}>
                          <TableRow
                            className="group cursor-pointer hover:bg-secondary/50 border-b border-border"
                            onClick={() => toggleMembershipRow(ms.id)}
                          >
                            <TableCell className="w-10">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleMembershipRow(ms.id);
                                }}
                              >
                                <ChevronDown
                                  className={`h-4 w-4 transition-transform duration-200 ${expandedMembershipRows[ms.id] ? "rotate-180" : ""}`}
                                />
                              </Button>
                            </TableCell>
                            <TableCell className="font-medium">
                              <div>
                                <p>{getPackageName(ms)}</p>
                                {ms.renewedFromPackageName && (
                                  <p className="text-[10px] text-muted-foreground font-mono mt-0.5">
                                    🔄 Renewed from: {ms.renewedFromPackageName}
                                  </p>
                                )}
                                {ms.renewedToPackageName && (
                                  <p className="text-[10px] text-muted-foreground font-mono mt-0.5">
                                    ➡️ Renewed to: {ms.renewedToPackageName}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <p className="font-mono">
                                  {ms.startDate?.slice(0, 10)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  to {ms.endDate?.slice(0, 10)}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>{renderAmount(ms)}</TableCell>
                            <TableCell>
                              <div>
                                <span className="text-emerald-500 font-mono">
                                  ₹{(ms.paidAmount || 0).toLocaleString()}
                                </span>
                                {ms.remainingAmount > 0 && (
                                  <p className="text-xs text-amber-500 font-mono mt-0.5">
                                    Due: ₹{ms.remainingAmount.toLocaleString()}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <Badge
                                  className={`${statusColor(ms.status)} border text-[10px] font-mono uppercase w-fit`}
                                >
                                  {ms.status}
                                </Badge>
                                {ms.status === "Partial" &&
                                  ms.partialPaymentDate && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-mono text-amber-500">
                                      <Clock className="h-3 w-3" />
                                      {ms.partialPaymentDate.slice(0, 10)}
                                    </span>
                                  )}
                              </div>
                            </TableCell>
                          </TableRow>
                          {expandedMembershipRows[ms.id] && (
                            <TableRow className="bg-secondary/30 hover:bg-secondary/30 border-b border-border animate-in fade-in duration-200">
                              <TableCell colSpan={6} className="p-0">
                                {renderMembershipDetails(ms)}
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* ── Mobile Card List (hidden on md+) ── */}
              <div
                className="md:hidden overflow-auto flex-1 divide-y divide-border"
                style={{ maxHeight: "calc(100vh - 340px)" }}
              >
                {memberships.length === 0 ? (
                  <p className="text-center py-12 text-muted-foreground text-sm">
                    No memberships found
                  </p>
                ) : (
                  memberships.map((ms, idx) => (
                    <Collapsible
                      key={ms.id}
                      open={expandedMembershipRows[ms.id]}
                      onOpenChange={() => toggleMembershipRow(ms.id)}
                      asChild
                    >
                      <div
                        className={` ${idx !== memberships.length - 1 ? "" : "border-b border-border"} hover:bg-secondary/10 transition-colors`}
                      >
                        <CollapsibleTrigger asChild>
                          <div className="flex items-center justify-between gap-2 cursor-pointer select-none px-3 py-1">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="flex items-center justify-between flex-col shrink-0">
                                <Avatar className="h-4 w-4 border border-border">
                                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-heading font-bold">
                                    <Package className="h-4 w-4 text-primary" />
                                  </AvatarFallback>
                                </Avatar>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-4 w-4 p-0 text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
                                >
                                  <ChevronDown
                                    className={`h-4 w-4 transition-transform duration-200 ${expandedMembershipRows[ms.id] ? "rotate-180" : ""}`}
                                  />
                                </Button>
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-sm leading-tight truncate">
                                  {getPackageName(ms)}
                                </p>
                                {ms.renewedFromPackageName && (
                                  <p className="text-[10px] text-muted-foreground font-mono mt-0.5">
                                    🔄 Renewed from: {ms.renewedFromPackageName}
                                  </p>
                                )}
                                {ms.renewedToPackageName && (
                                  <p className="text-[10px] text-muted-foreground font-mono mt-0.5">
                                    ➡️ Renewed to: {ms.renewedToPackageName}
                                  </p>
                                )}
                                <p className="text-[11px] text-muted-foreground font-mono mt-1">
                                  {ms.startDate?.slice(0, 10)} to{" "}
                                  {ms.endDate?.slice(0, 10)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <div className="flex items-end flex-col gap-0.5 text-right">
                                <Badge
                                  className={`${statusColor(ms.status)} border text-[9px] font-mono uppercase py-0 px-1.5`}
                                >
                                  {ms.status}
                                </Badge>
                                {ms.status === "Partial" &&
                                  ms.partialPaymentDate && (
                                    <span className="text-[9px] font-mono text-muted-foreground">
                                      {ms.partialPaymentDate.slice(0, 10)}
                                    </span>
                                  )}
                                <div className="mt-0.5 leading-none font-mono">
                                  <p className="font-semibold text-[10px] text-foreground">
                                    ₹{(ms.paidAmount || 0).toLocaleString()}
                                  </p>
                                  {ms.remainingAmount > 0 && (
                                    <p className="text-[9px] text-amber-500 mt-0.5">
                                      Due: ₹
                                      {ms.remainingAmount.toLocaleString()}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="border-t border-border/50 bg-secondary/5 animate-in fade-in slide-in-from-top-1 duration-200">
                            {renderMembershipDetails(ms)}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>
        )}

        {/* ── Payments Tab ── */}
        {canViewPayments && (
          <TabsContent value="payments">
            <Card
              className="bg-card border-border overflow-hidden flex flex-col"
              style={{ minHeight: "400px" }}
            >
              <CardHeader className="pb-3 py-4 px-4 border-b">
                <CardTitle className="text-sm font-heading uppercase tracking-wide">
                  Payment History
                </CardTitle>
              </CardHeader>

              {/* ── Desktop table (hidden below md) ── */}
              <div
                className="hidden md:block overflow-auto flex-1"
                style={{ maxHeight: "calc(100vh - 340px)" }}
              >
                <Table>
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Package</TableHead>
                      <TableHead>Mode</TableHead>
                      <TableHead>Transaction</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          className="text-center py-8 text-muted-foreground"
                        >
                          No payments recorded
                        </TableCell>
                      </TableRow>
                    ) : (
                      payments.map((p) => (
                        <Fragment key={p.id}>
                          <TableRow
                            className="group cursor-pointer hover:bg-secondary/50 border-b border-border"
                            onClick={() => togglePaymentRow(p.id)}
                          >
                            <TableCell className="w-10">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  togglePaymentRow(p.id);
                                }}
                              >
                                <ChevronDown
                                  className={`h-4 w-4 transition-transform duration-200 ${expandedPaymentRows[p.id] ? "rotate-180" : ""}`}
                                />
                              </Button>
                            </TableCell>
                            <TableCell>{renderPaymentDate(p.date)}</TableCell>
                            <TableCell>
                              {p.packageName ? (
                                <span className="inline-flex items-center gap-1 text-sm font-medium">
                                  <Package className="h-3.5 w-3.5 text-primary shrink-0" />
                                  {p.packageName}
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-xs">
                                  —
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {p.paymentMode}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {p.transactionId || "-"}
                            </TableCell>
                            <TableCell className="font-mono font-bold text-emerald-500">
                              ₹{p.amount?.toLocaleString()}
                            </TableCell>
                            <TableCell className="max-w-[180px]">
                              {p.notes ? (
                                <span
                                  className="text-sm text-muted-foreground truncate block"
                                  title={p.notes}
                                >
                                  {p.notes}
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-xs">
                                  —
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                          {expandedPaymentRows[p.id] && (
                            <TableRow className="bg-secondary/30 hover:bg-secondary/30 border-b border-border animate-in fade-in duration-200">
                              <TableCell colSpan={7} className="p-0">
                                {renderPaymentDetails(p)}
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* ── Mobile Card List (hidden on md+) ── */}
              <div
                className="md:hidden overflow-auto flex-1 divide-y divide-border"
                style={{ maxHeight: "calc(100vh - 340px)" }}
              >
                {payments.length === 0 ? (
                  <p className="text-center py-12 text-muted-foreground text-sm">
                    No payments recorded
                  </p>
                ) : (
                  payments.map((p, idx) => (
                    <Collapsible
                      key={p.id}
                      open={expandedPaymentRows[p.id]}
                      onOpenChange={() => togglePaymentRow(p.id)}
                      asChild
                    >
                      <div
                        className={` ${idx !== payments.length - 1 ? "" : "border-b border-border"} hover:bg-secondary/10 transition-colors`}
                      >
                        <CollapsibleTrigger asChild>
                          <div className="flex items-center justify-between gap-2 cursor-pointer select-none px-3 py-1">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="flex items-center justify-between flex-col shrink-0">
                                <Avatar className="h-4 w-4 border border-border">
                                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-heading font-bold">
                                    <DollarSign className="h-4 w-4 text-emerald-500" />
                                  </AvatarFallback>
                                </Avatar>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-4 w-4 p-0 text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
                                >
                                  <ChevronDown
                                    className={`h-3 w-3 size-3 transition-transform duration-200 ${expandedPaymentRows[p.id] ? "rotate-180" : ""}`}
                                  />
                                </Button>
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-sm leading-tight truncate">
                                  {p.packageName || "Membership Payment"}
                                </p>
                                <p className="text-[11px] text-muted-foreground font-mono mt-1">
                                  {p.date?.slice(0, 10)}{" "}
                                  {p.date?.length > 10
                                    ? `· ${formatTimeAMPM(p.date.slice(11, 16))}`
                                    : ""}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <div className="flex items-end flex-col gap-0.5 text-right">
                                <Badge
                                  variant="outline"
                                  className="text-[9px] font-mono uppercase py-0 px-1.5"
                                >
                                  {p.paymentMode}
                                </Badge>
                                <div className="mt-1 leading-none font-mono">
                                  <p className="font-bold text-sm text-emerald-500">
                                    ₹{p.amount?.toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="border-t border-border/50 bg-secondary/5 animate-in fade-in slide-in-from-top-1 duration-200">
                            {renderPaymentDetails(p)}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>
        )}

        {/* ── Attendance Tab ── */}
        {canViewAttendance && (
          <TabsContent value="attendance">
            <Card className="bg-card border-border flex flex-col overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between gap-3 py-2 px-4 border-b">
                <CardTitle className="text-sm font-heading uppercase tracking-wide">
                  Recent Attendance
                </CardTitle>
                <Button
                  size="sm"
                  onClick={() => setShowAttendanceDialog(true)}
                  className="text-xs font-heading uppercase tracking-wide shrink-0 font-medium mt-0"
                  data-testid="log-attendance-btn"
                >
                  <Plus className="h-4 w-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Log Attendance</span>
                  <span className="sm:hidden">Log</span>
                </Button>
              </CardHeader>
              {attendanceLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
                    <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
                      Loading
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="hidden md:block overflow-x-auto scrollbar-hide">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8 text-[10px] font-mono uppercase tracking-widest">
                            #
                          </TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Check In</TableHead>
                          <TableHead>Check Out</TableHead>
                          <TableHead>Duration</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {attendance.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              className="text-center py-8 text-muted-foreground"
                            >
                              No attendance records
                            </TableCell>
                          </TableRow>
                        ) : (
                          attendance.map((a, idx) => (
                            <TableRow key={a.id}>
                              <TableCell className="text-[10px] font-mono text-muted-foreground">
                                {(attendancePage - 1) * attendancePageSize +
                                  idx +
                                  1}
                              </TableCell>
                              <TableCell className="font-mono">
                                {a.date?.slice(0, 10)}
                              </TableCell>
                              <TableCell className="font-mono">
                                {a.checkIn
                                  ? new Date(a.checkIn).toLocaleTimeString(
                                      "en-IN",
                                      { hour: "2-digit", minute: "2-digit" },
                                    )
                                  : "-"}
                              </TableCell>
                              <TableCell className="font-mono">
                                {a.checkOut
                                  ? new Date(a.checkOut).toLocaleTimeString(
                                      "en-IN",
                                      { hour: "2-digit", minute: "2-digit" },
                                    )
                                  : "-"}
                              </TableCell>
                              <TableCell className="font-mono">
                                {(() => {
                                  if (a.duration)
                                    return `${Math.floor(a.duration / 60)}h ${a.duration % 60}m`;
                                  if (!a.checkIn || !a.checkOut) return "-";
                                  const diff =
                                    new Date(a.checkOut) - new Date(a.checkIn);
                                  if (isNaN(diff) || diff < 0) return "-";
                                  const mins = Math.floor(diff / 60000);
                                  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
                                })()}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="md:hidden divide-y divide-border">
                    {attendance.length === 0 ? (
                      <p className="text-center py-8 text-muted-foreground text-sm">
                        No attendance records
                      </p>
                    ) : (
                      attendance.map((a) => (
                        <div
                          key={a.id}
                          className="px-4 py-3 flex items-center justify-between gap-2"
                        >
                          <div>
                            <p className="font-mono text-sm font-medium">
                              {a.date?.slice(0, 10)}
                            </p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {a.checkIn
                                ? new Date(a.checkIn).toLocaleTimeString(
                                    "en-IN",
                                    { hour: "2-digit", minute: "2-digit" },
                                  )
                                : "—"}{" "}
                              →{" "}
                              {a.checkOut
                                ? new Date(a.checkOut).toLocaleTimeString(
                                    "en-IN",
                                    { hour: "2-digit", minute: "2-digit" },
                                  )
                                : "—"}
                            </p>
                          </div>
                          <Badge
                            variant="outline"
                            className="font-mono text-xs shrink-0"
                          >
                            {(() => {
                              if (a.duration)
                                return `${Math.floor(a.duration / 60)}h ${a.duration % 60}m`;
                              if (!a.checkIn || !a.checkOut) return "—";
                              const diff =
                                new Date(a.checkOut) - new Date(a.checkIn);
                              if (isNaN(diff) || diff < 0) return "—";
                              const mins = Math.floor(diff / 60000);
                              return `${Math.floor(mins / 60)}h ${mins % 60}m`;
                            })()}
                          </Badge>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Pagination */}
                  <div className="flex flex-row items-center justify-between gap-4 p-3 border-t border-border shrink-0">
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span className="font-mono hidden sm:block">
                        {attendanceTotal} total
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs">Show:</span>
                        <Select
                          value={String(attendancePageSize)}
                          onValueChange={(v) => {
                            setAttendancePageSize(Number(v));
                            setAttendancePage(1);
                          }}
                        >
                          <SelectTrigger className="w-20 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAGE_SIZE_OPTIONS.map((n) => (
                              <SelectItem key={n} value={String(n)}>
                                {n}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={attendancePage === 1}
                        onClick={() => setAttendancePage((p) => p - 1)}
                        data-testid="prev-page-btn"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="sm:text-sm text-xs text-muted-foreground px-1">
                        Page {attendancePage} of{" "}
                        {Math.max(
                          1,
                          Math.ceil(attendanceTotal / attendancePageSize),
                        )}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={
                          attendancePage >=
                          Math.max(
                            1,
                            Math.ceil(attendanceTotal / attendancePageSize),
                          )
                        }
                        onClick={() => setAttendancePage((p) => p + 1)}
                        data-testid="next-page-btn"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Card>
          </TabsContent>
        )}

        <TabsContent value="progress">
          <MemberProgressTab member={member} />
        </TabsContent>
        {canViewWorkouts && (
          <TabsContent value="workout">
            <MemberWorkoutTab member={member} />
          </TabsContent>
        )}
        {canViewFollowups && (
          <TabsContent value="followups">
            <MemberFollowupsTab member={member} />
          </TabsContent>
        )}
      </Tabs>

      {canAddMembership && (
        <MembershipModal
          open={showMembershipModal}
          onOpenChange={(open) => {
            setShowMembershipModal(open);
            if (!open) {
              setEditMembership(null);
              setIsRenewalModal(false);
            }
          }}
          member={member}
          membership={editMembership}
          isRenewal={isRenewalModal}
          packages={packages}
          branchId={member.branchId}
          onSuccess={fetchMember}
        />
      )}
      {canAddPayment && (
        <AddPaymentModal
          open={showPaymentModal}
          onOpenChange={setShowPaymentModal}
          membership={paymentMembership}
          packageName={
            paymentMembership ? getPackageName(paymentMembership) : ""
          }
          onSuccess={fetchMember}
        />
      )}
      {canViewAttendance && (
        <QuickAttendanceDialog
          open={showAttendanceDialog}
          onOpenChange={setShowAttendanceDialog}
          member={member}
          branchId={member.branchId}
          onSuccess={fetchAttendance}
        />
      )}
    </div>
  );
}
